import { useNavigate } from "#routers/hooks";
import { useIsFeatureAvailable } from "#src/contexts/AuthenticatedContext.helpers";
import { linkToInsightsDetails } from "#src/routes/dashboard/insights/[insightsId]/details";
import {
  fetchFileDownloadMetaWithScanPolling,
  getFileDownloadMetaRefetchInterval,
} from "#src/utils/fileDownload";
import { useQueries, useQuery } from "@tanstack/react-query";
import { CardGrid } from "@validereinc/common-components";
import {
  Insights,
  InsightsAdapterQueryKeys,
  type FileDownloadMetaType,
} from "@validereinc/domain";
import { FeatureFlags, PermissionsMap } from "@validereinc/domain-controllers";
import { dateFormatter } from "@validereinc/utilities";
import React from "react";
import { InsightsDashboardThumbnailPlaceholder } from "./InsightsDashboardThumbnailPlaceholder";

const THUMBNAIL_URL_CACHE_TIME_MS = 5 * 60 * 1000;

export const InsightsDashboardTab = () => {
  const navigate = useNavigate();

  const [shouldUseV2Insights] = useIsFeatureAvailable({
    featureFlagQuery: {
      $and: [FeatureFlags.DashboardsManageInsights],
    },
    permissionQuery: PermissionsMap.InsightsDashboardsRead,
  });

  const { data: dashboardsData, isLoading: isLoadingDashboards } = useQuery({
    queryKey: InsightsAdapterQueryKeys.getDashboards({}),
    queryFn: () => Insights.getDashboards({}),
    enabled: !shouldUseV2Insights,
  });

  const { data: dashboardsDataV2, isLoading: isLoadingDashboardsV2 } = useQuery(
    {
      queryKey: InsightsAdapterQueryKeys.getDashboardsV2({}),
      queryFn: () => Insights.getDashboardsV2({}),
      enabled: shouldUseV2Insights,
    }
  );

  const onPrimaryCardClick = (id: string) => {
    navigate({ pathname: linkToInsightsDetails(id) });
  };

  const dashboards = shouldUseV2Insights ? dashboardsDataV2 : dashboardsData;
  const isLoading = shouldUseV2Insights
    ? isLoadingDashboardsV2
    : isLoadingDashboards;

  const thumbnailQueries = useQueries({
    queries: (dashboards ?? []).map((dashboard) => ({
      queryKey: InsightsAdapterQueryKeys.getDashboardThumbnail({
        thumbnailRef: dashboard.thumbnail_ref,
      }),
      queryFn: ({ signal }: { signal?: AbortSignal }) =>
        fetchFileDownloadMetaWithScanPolling(dashboard.thumbnail_ref!, {
          signal,
        }),
      enabled: Boolean(dashboard.thumbnail_ref),
      retry: false,
      refetchOnWindowFocus: false,
      // refresh the pre-signed URL before it expires so a long-lived cache
      // entry never serves an expired (broken) thumbnail link
      refetchInterval: (data?: FileDownloadMetaType) =>
        getFileDownloadMetaRefetchInterval(data),
      cacheTime: THUMBNAIL_URL_CACHE_TIME_MS,
      meta: { hideErrorToasts: true },
    })),
  });

  const items = (dashboards ?? []).map((dashboard, index) => ({
    ...dashboard,
    image: thumbnailQueries[index]?.data?.uri,
  }));

  return (
    <CardGrid
      items={items}
      onPrimaryCardClick={onPrimaryCardClick}
      hasCta={false}
      isLoading={isLoading}
      getMeta={({ updatedAt }: { updatedAt?: Date }) => {
        if (updatedAt) {
          return `Last updated: ${dateFormatter(updatedAt)}`;
        }

        return "";
      }}
      listCardType="image"
      imagePlaceholder={<InsightsDashboardThumbnailPlaceholder />}
    />
  );
};
